import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, CornerDownLeft } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { flatNavigation } from '@/lib/navigation'
import { cn } from '@/lib/utils'

/**
 * Ctrl/Cmd-K launcher over every page in the app.
 *
 * With ~50 pages the sidebar alone is a poor way to find things; this matches on
 * page name, its group, and per-page keywords (so "tvpi" finds Private Markets
 * and "ledger" finds the Portfolio Book).
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { user } = useAuthStore()

  // open with Ctrl/Cmd-K, or when the sidebar button asks
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(o => !o)
      } else if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    const onOpen = () => setOpen(true)
    window.addEventListener('keydown', onKey)
    window.addEventListener('plutus:open-command-palette', onOpen as EventListener)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('plutus:open-command-palette', onOpen as EventListener)
    }
  }, [])

  useEffect(() => {
    if (open) {
      setQuery(''); setActive(0)
      setTimeout(() => inputRef.current?.focus(), 30)
    }
  }, [open])

  const items = useMemo(
    () => flatNavigation.filter(i => !i.adminOnly || user?.role === 'Admin'),
    [user?.role]
  )

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items.slice(0, 12)
    const scored = items
      .map(i => {
        const name = i.name.toLowerCase()
        const hay = `${name} ${i.section.toLowerCase()} ${i.keywords || ''}`
        if (!hay.includes(q)) return null
        // exact/prefix matches on the page name rank highest
        const score = name === q ? 0 : name.startsWith(q) ? 1 : name.includes(q) ? 2 : 3
        return { item: i, score }
      })
      .filter(Boolean) as { item: typeof items[number]; score: number }[]
    scored.sort((a, b) => a.score - b.score || a.item.name.localeCompare(b.item.name))
    return scored.slice(0, 12).map(s => s.item)
  }, [query, items])

  useEffect(() => { setActive(0) }, [query])

  if (!open) return null

  const go = (href: string) => { setOpen(false); navigate(href) }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, results.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)) }
    else if (e.key === 'Enter' && results[active]) { e.preventDefault(); go(results[active].href) }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
      <div className="mt-24 w-full max-w-xl overflow-hidden rounded-lg bg-card shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-border px-4">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search pages…  (try: tvpi, ledger, qis, var)"
            className="w-full bg-transparent py-3 text-sm outline-none"
          />
          <kbd className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">Esc</kbd>
        </div>

        <div className="max-h-80 overflow-y-auto py-1">
          {results.map((r, i) => (
            <button
              key={r.href + r.name}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(r.href)}
              className={cn(
                'flex w-full items-center gap-3 px-4 py-2 text-left text-sm',
                i === active ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              )}
            >
              <r.icon className="h-4 w-4 shrink-0" />
              <span className="flex-1">{r.name}</span>
              <span className={cn('text-xs', i === active ? 'text-primary-foreground/70' : 'text-muted-foreground')}>
                {r.section}
              </span>
              {i === active && <CornerDownLeft className="h-3 w-3" />}
            </button>
          ))}
          {!results.length && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No pages match “{query}”.</div>
          )}
        </div>
      </div>
    </div>
  )
}
